import getFiltersFromGrid from '@/features/filters/helpers/getFiltersFromGrid';
import {
  FilterBlockType,
  IExtendedFilterGridItem,
  IFilterGrid,
  IFilterGridItem,
} from '@/interfaces/constructor.interfaces';
import { IFiltersData, IFilterValues } from '@/interfaces/filters.interfaces';
import instanceOfGrid from '@/utils/instanceOfGrid';

export type ExcludeConditionFunction = (
  item: IFilterGridItem,
  values: IFilterValues,
) => boolean;

/**
 * Возвращает список фильтров, которые можно исключить из отображения.
 * Исключаемы фильтр должен удовлетворять условиям:
 * - Должны не иметь значаний
 * или
 * - Должен иметь настройку 'hideSingleOption'
 * - Должен не быть зависимостью для других фильтров
 * - Должен быть типа фильтра, который можно исключить (например, не должен быть полем ввода)
 * - Должен иметь один вариант значения
 * @param filterItems список фильтров, для проверки условий
 * @param data данные для фильтра
 */
function getExcludingItems(
  filterItems: Array<IFilterGridItem>,
  data: IFiltersData,
  excludeCondition?: ExcludeConditionFunction,
) {
  const filtersToRemove = filterItems.reduce((acc, item) => {
    const filterValues = data[item.key];

    if (excludeCondition && excludeCondition(item, filterValues)) {
      return [...acc, item];
    }

    // Значения для фильтра отсутствуют
    const isMissingValues =
      !filterValues ||
      (Array.isArray(filterValues) && !filterValues.length) ||
      (!Array.isArray(filterValues) && !filterValues.min && !filterValues.max);

    if (isMissingValues) {
      return [...acc, item];
    }

    // Если у фильтра не установлена настройка исключения, то такой фильтр удалять нельзя.
    const isHideSingleOption = 'hideSingleOption' in item && item.hideSingleOption;
    const isDependency = isItemDependency(item, filterItems);
    const isInIncludeList = [
      FilterBlockType.FilterLocation,
      FilterBlockType.FilterCheckboxGroup,
      FilterBlockType.FilterModelCheckboxGroup,
      FilterBlockType.FilterColorPicker,
      FilterBlockType.FilterMultipleSelect,
      FilterBlockType.FilterSelect,
    ].includes(item.type);

    if (!Array.isArray(filterValues)) {
      return acc;
    }

    const isSingleValue = [...filterValues].length <= 1;

    if (isHideSingleOption && !isDependency && isInIncludeList && isSingleValue) {
      return [...acc, item];
    }

    return acc;
  }, [] as Array<IFilterGridItem>);

  return filtersToRemove;
}

/**
 * Возвращает набор фильтров, с удаленными пустыми группами.
 * @param items набор фильтров
 */
function removeEmptyFilterGroups(
  items: IExtendedFilterGridItem[],
): IExtendedFilterGridItem[] {
  return items.filter(item => {
    // Если блок фильтра задан пустой группой, исключаем его.
    if ('items' in item && Array.isArray(item.items) && item.items.length === 0) {
      return false;
    }

    return true;
  });
}

/**
 * Возвращает грид фильтров с удаленными фильтрами, переданными в качестве параметра.
 * @param grid грид из которого будут удалены фильтры
 * @param removeItems фильтры, которые нужно удалить
 */
function removeItemsFromGrid(
  grid: IFilterGrid,
  removeItems: Array<IFilterGridItem>,
): IFilterGrid {
  // Отфильтровать элементы грида.
  const gridItems: Array<IExtendedFilterGridItem> = [];
  grid.items.forEach(item => {
    if (instanceOfGrid(item)) {
      const subgrid = removeItemsFromGrid(item as IFilterGrid, removeItems);
      gridItems.push(subgrid);
      return;
    }

    const gridItem = item;

    if (!('key' in gridItem)) {
      gridItems.push(gridItem);
    }

    const isRemoveItem = removeItems.some(removeItem => {
      if ('key' in removeItem && 'key' in gridItem) {
        return removeItem.key === gridItem.key;
      }

      return false;
    });

    if (!isRemoveItem) {
      gridItems.push(gridItem);
    }
  });

  const gridItemsWithoutEmpty = removeEmptyFilterGroups(gridItems);

  return {
    ...grid,
    items: gridItemsWithoutEmpty,
  };
}

/**
 * Является ли переданный фильтр зависимостью для других
 * @param item проверяемый фильтр
 * @param items набор фильтров, в которых происходит поиск
 * @returns true, если фильтр является зависимостью для других
 */
function isItemDependency(item: IFilterGridItem, items: Array<IFilterGridItem>): boolean {
  const isDependency = items.some(i => {
    if ('dependencies' in i && Array.isArray(i.dependencies)) {
      return i.dependencies.includes(item.key);
    }

    return false;
  });

  return isDependency;
}

/**
 * Возвращает грид фильтров с исключенными элементами, соответствующими условиям:
 * - у фильтра отсутствуют значения
 * или
 * - у фильтра установлен параметр hideSingleOption
 * - фильтр содержит одно значение
 * - фильтр не является зависимостью для других (иначе его значение должно быть выбрано и тогда он отобразиться в истории выбора)
 * - тип фильтра подходит для выбора одного значения (например, не является полем ввода)
 * @param grid грид фильтров
 * @param data данные для фильтров
 * @returns грид, с исключенными фильтрами
 */
export const excludeInvalidFilterGrid = (
  grid: IFilterGrid,
  data: IFiltersData,
  excludeCondition?: ExcludeConditionFunction,
): IFilterGrid => {
  // Собираем плоский массив фильтров в гриде
  const filterItems = getFiltersFromGrid(grid);
  // Находим список фильтров, которые нужно удалить
  const filterItemsToRemove = getExcludingItems(filterItems, data, excludeCondition);
  // Удаляем фильтры из грида
  const filteredGrid = removeItemsFromGrid(grid, filterItemsToRemove);

  return filteredGrid;
};

export default excludeInvalidFilterGrid;
